import _ from 'lodash'
import { useCallback, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { useRequestHandler } from '@/hooks/useRequestHandler'

import useMediaDevices from '@/hooks/useMediaDevices'

import { CreateCustomDeviceDto } from '@/core/models/custom-device.model'

import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { createCustomDevice, selectCustomDeviceCreateRequestState, selectCustomDevices } from '@/redux/reducers/custom-device.reducer'

import CustomDeviceForm, { CustomDeviceFormData, CustomDeviceFormHandle } from '../components/CustomDeviceForm'

type DeviceFormContainerProps = {}

export default function DeviceFormContainer(props: DeviceFormContainerProps) {
  const mediaDevices = useMediaDevices()
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  const formRef = useRef<CustomDeviceFormHandle>(null)

  const { dispatch, requestTracker: customDeviceCreateRequestTracker } = useRequestHandler({
    onSuccess: () => {
      enqueueSnackbar(t('notification.customDevice.create.success'), { variant: 'success' })
      formRef.current?.reset()
    },
    onError: () => enqueueSnackbar(t('notification.customDevice.create.error'), { variant: 'error' }),
    requestTrackerSelector: selectCustomDeviceCreateRequestState,
  })

  const authUser = useAppSelector(selectAuthUser)
  const customDevices = useAppSelector(selectCustomDevices)

  const filteredMediaDevices = useMemo(
    () =>
      _.filter(mediaDevices, (mediaDevice) => {
        if (!customDevices.length) return true

        return !_.find(customDevices, (customDevice) => customDevice.device.name === mediaDevice.label)
      }),
    [customDevices, mediaDevices]
  )

  const transformData = (authUserId: string, deviceFormValues: CustomDeviceFormData, mediaDevices: MediaDeviceInfo[]): CreateCustomDeviceDto => {
    let mediaDevice = mediaDevices.find((device) => device.deviceId === deviceFormValues.device)!
    let data = {
      device: {
        deviceId: mediaDevice.deviceId,
        name: mediaDevice.label,
        type: mediaDevice.kind,
      },
      customName: deviceFormValues.customName,
      owner: authUserId,
      iconURL: deviceFormValues.iconURL,
      ptz: deviceFormValues.ptz,
      type: deviceFormValues.type,
    }
    return data
  }

  const handleCreateCustomDevice = useCallback(
    (values: CustomDeviceFormData) => {
      if (!authUser) return
      const createCustomDeviceDto = transformData(authUser._id, values, mediaDevices)
      dispatch(createCustomDevice(createCustomDeviceDto))
    },
    [dispatch, authUser, mediaDevices]
  )

  return <CustomDeviceForm ref={formRef} devices={filteredMediaDevices} isSubmitting={customDeviceCreateRequestTracker.loading} onSubmit={handleCreateCustomDevice} />
}
